import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Privacy </span>
          <span className="text-[#00FF41]">Policy</span>
        </h1>
        <p className="text-gray-500 text-sm mb-8">Last updated: May 14, 2026</p>

        <div className="space-y-6 text-sm text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. Introduction</h2>
            <p>Welcome to BitBot ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we handle information. This Privacy Policy explains our practices regarding any information associated with your use of our automated cryptocurrency trading service at xbitbotx.com (the "Service").</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Information We Collect</h2>
            <h3 className="text-sm font-bold text-gray-200 mt-3 mb-1">2.1 Information You Provide</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><span className="text-gray-300">Account Information:</span> Email address and name when you sign up via Google OAuth.</li>
              <li><span className="text-gray-300">Exchange API Keys:</span> Your Coinbase API credentials for executing trades. These are stored encrypted and never shared.</li>
              <li><span className="text-gray-300">Bot Configuration:</span> Your trading preferences, strategy settings, and notification preferences.</li>
              <li><span className="text-gray-300">Telegram Configuration:</span> Bot token and chat ID if you enable Telegram notifications.</li>
            </ul>
            <h3 className="text-sm font-bold text-gray-200 mt-3 mb-1">2.2 Information Collected Automatically</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><span className="text-gray-300">Trading Data:</span> Records of all trades executed by the bot (buy/sell prices, amounts, timestamps, profit/loss).</li>
              <li><span className="text-gray-300">Portfolio Data:</span> Your cryptocurrency balances and portfolio values, synced from your exchange.</li>
              <li><span className="text-gray-300">Device Information:</span> Basic browser and device information through standard web server logs.</li>
              <li><span className="text-gray-300">Usage Data:</span> How you interact with the dashboard, features used, and session information.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>To execute automated trades on your behalf via the Coinbase API</li>
              <li>To calculate and display portfolio performance, profit/loss, and cost basis</li>
              <li>To send trade alerts, daily summaries, and notifications via email and Telegram</li>
              <li>To display leaderboard rankings and user statistics</li>
              <li>To maintain and improve the trading algorithms and service reliability</li>
              <li>To provide the AI Assistant with context about your portfolio for personalized answers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Information Sharing</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share information in the following limited circumstances:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400 mt-2">
              <li><span className="text-gray-300">Leaderboard:</span> Your name and trading statistics are visible to other users on the leaderboard.</li>
              <li><span className="text-gray-300">Exchange APIs:</span> Trade orders are sent to Coinbase to execute on your behalf.</li>
              <li><span className="text-gray-300">AI Services:</span> Anonymous portfolio data is sent to AI providers for the assistant feature.</li>
              <li><span className="text-gray-300">Legal Requirements:</span> We may disclose information if required by law or to protect our rights and safety.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Data Security</h2>
            <p>We implement appropriate security measures including HTTPS encryption, secure session management, and encrypted storage of sensitive credentials. Exchange API keys are stored with encryption at rest. However, no method of transmission over the Internet is 100% secure, and cryptocurrency trading carries inherent financial risk.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Financial Disclaimer</h2>
            <p className="text-yellow-500">BitBot is an automated trading tool, not a financial advisor. Cryptocurrency trading involves substantial risk of loss. Past performance does not guarantee future results. You are solely responsible for your trading decisions and any financial losses incurred. Only trade with funds you can afford to lose.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Cookies and Tracking</h2>
            <p>We use session cookies to maintain your login state and preferences. We may use analytics services to understand how users interact with the Service. You can control cookie preferences through your browser settings.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Data Retention</h2>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><span className="text-gray-300">Trade History:</span> Retained for the lifetime of your account for tax reporting and performance analysis.</li>
              <li><span className="text-gray-300">Portfolio Snapshots:</span> Retained for historical performance tracking.</li>
              <li><span className="text-gray-300">Chat History:</span> AI assistant conversations retained until you clear them.</li>
              <li><span className="text-gray-300">Account Data:</span> Retained until you request deletion.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Your Rights</h2>
            <p>Depending on your location, you may have rights to access, correct, delete, or port your personal information. To exercise these rights, contact us at <a href="mailto:eviltim@gmail.com" className="text-[#00FF41] hover:text-white">eviltim@gmail.com</a>.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Children's Privacy</h2>
            <p>Our Service is not directed to children under 18. Cryptocurrency trading is restricted to adults. We do not knowingly collect information from minors.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Changes will be reflected in the "Last updated" date above. Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">12. Contact Us</h2>
            <p>If you have questions about this Privacy Policy:</p>
            <p className="mt-2 text-gray-400"><span className="text-white font-bold">BitBot</span><br/>Email: <a href="mailto:eviltim@gmail.com" className="text-[#00FF41] hover:text-white">eviltim@gmail.com</a><br/>Website: <a href="https://xbitbotx.com" className="text-[#00FF41] hover:text-white">xbitbotx.com</a></p>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00]/30 flex flex-wrap gap-4 text-xs text-gray-600">
          <a href="/about" className="hover:text-[#00FF41]">About</a>
          <a href="/terms" className="hover:text-[#00FF41]">Terms of Service</a>
          <a href="/faq" className="hover:text-[#00FF41]">FAQ</a>
          <a href="/changelog" className="hover:text-[#00FF41]">Changelog</a>
        </footer>
      </div>
    </div>
  );
}
